import React, { useMemo } from 'react';
import {
  BreakpointQueryName, Button, ButtonColor, ButtonSize, ButtonVariant, SpacingName, TypographyName,
} from '@doveit/lego';
import { useInView } from 'react-hook-inview';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import BottomBar, { BottomBarProps } from '../client/templates/common/bottom-bar/BottomBar';
import { goToPage, buildEvaluationURLObject } from '../client/utils/links';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import Stripe, { StripeDirection } from '../client/components/stripe/Stripe';
import { Feature, TextAlign } from '../client/types';
import PageSection from '../client/components/page-section/PageSection';
import { MiniInfoBlockProps } from '../client/components/mini-info-block/MiniInfoBlock';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import Intro, { IntroProps } from '../client/components/intro/Intro';
import MiniInfoBlockList from '../client/components/mini-info-block-list/MiniInfoBlockList';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import * as styles from '../client/templates/our-services/OurServices.style';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface OurServicesProps {
  SEO: PageHeadProps['SEO'],
  intro: IntroProps,
  featuresSection: {
    features: Feature[],
  },
  advantages: {
    title: string,
    description: string,
    items: MiniInfoBlockProps[],
  },
  form: {
    title: string,
    intro: string,
    label: string,
  },
  bottomBar: BottomBarProps,
}

const INTERSECTION_OPTIONS = { threshold: 0 };

export const OurServices: NextPage<OurServicesProps> = ({
  SEO,
  intro,
  featuresSection,
  advantages,
  bottomBar,
  form,
}) => {
  const [onlineEvaluationRef, isTargetVisible] = useInView(INTERSECTION_OPTIONS);

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [{
    label: 'I nostri servizi',
  }], []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
        )}
      />
      <PageLayout>
        <PageLayout.Wrapper maxWidth={PageLayoutSize.L}>
          <PageLayout.Breadcrumb items={breadcrumb} />
          <PageLayout.Header>
            <Intro
              title={intro.title}
              description={intro.description}
              image={intro.image}
            />
          </PageLayout.Header>
        </PageLayout.Wrapper>
        <PageSection padding={[SpacingName.BIGGER, 0]}>
          <PageSection.Wrapper>
            {featuresSection.features.map((feature, index) => (
              <section
                key={feature.id}
                ref={feature.id === 'online-evaluation' ? onlineEvaluationRef : undefined}
                css={styles.feature}
                isFirst={index === 0}
              >
                <Stripe
                  title={feature.title}
                  description={feature.description}
                  direction={(index % 2 === 0
                    ? StripeDirection.RIGHT_TO_LEFT
                    : StripeDirection.LEFT_TO_RIGHT)}
                  picture={{
                    src: requireFromStaticAsset(feature.picture),
                    alt: `Icona ${feature.title}`,
                  }}
                >
                  <Button
                    label={feature.cta.label}
                    size={ButtonSize.SMALL}
                    variant={feature.cta.highlight ? ButtonVariant.FILLED : ButtonVariant.LIGHT}
                    color={feature.cta.highlight ? ButtonColor.SECONDARY : ButtonColor.PRIMARY}
                    withoutSpacing={!feature.cta.highlight}
                    href={feature.cta.urlObject.as}
                    onClick={() => goToPage(feature.cta.urlObject)}
                  />
                </Stripe>
              </section>
            ))}
          </PageSection.Wrapper>
        </PageSection>
        <PageSection
          backgroundColor="neutrals.lighter"
          padding={[SpacingName.BIG, 0, SpacingName.BIGGER]}
        >
          <PageSection.Wrapper>
            <PageSection.Title
              content={advantages.title}
            />
            <PageSection.Description
              text={advantages.description}
              size={TypographyName.H4}
              color="neutrals.medium"
            />
            <PageSection.Block>
              <MiniInfoBlockList items={advantages.items} />
            </PageSection.Block>
          </PageSection.Wrapper>
        </PageSection>

        <PageSection>
          <PageSection.Wrapper>
            <PageSection.Title
              content={form.title}
              textAlign={TextAlign.CENTER}
            />
            <PageSection.Description
              text={form.intro}
              textAlign={TextAlign.CENTER}
              size={TypographyName.H4}
              color="neutrals.dark"
            />
            <CreateLeadSection
              title={form.label}
              dataRef="lead-form-main"
              onSuccess={(lead) => sendLeadSubmitEventAndRedirect('main', lead)}
              dataTracking="lead-form-main-submit"
            />
          </PageSection.Wrapper>
        </PageSection>
        <BottomBar
          hiddenFrom={BreakpointQueryName.MD}
          expanded={!isTargetVisible}
          label={bottomBar.label}
          title={bottomBar.title}
          headline={bottomBar.headline}
          onClick={() => goToPage(buildEvaluationURLObject())}
        />
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<OurServicesProps>(async () => {
  const [common, pageData] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/our-services.yml'),
  ]);

  return {
    props: {
      ...common,
      ...pageData,
    },
  };
});

export default withError(OurServices);
